import { AbeetContextValue, booleanResultResolver } from '@clutter/abeet';
import { useQueryParams } from '@clutter/hooks';

import { CheckoutVersion } from '@root/components/checkout/types';
import { WT_VISITOR_TOKEN, WT_VISITOR_TOKEN_KEY } from '@root/initializers/wt';

import { useAbeetVisitorTest } from '@shared/abeet';

/** Abeet family for classic vs challenger storage booking funnel (ENG-1021). */
const STORAGE_BOOKING_FUNNEL_TEST = 'web_visitor_storage_booking_funnel';

/** Abeet variant names (also used with `?abeet[web_visitor_storage_booking_funnel]=…`). */
const STORAGE_BOOKING_FUNNEL_CONTROL = 'control';
const STORAGE_BOOKING_FUNNEL_CHALLENGER = 'challenger';

/**
 * Convenience preview override (production-ok for QA).
 * Examples: `?force_booking_funnel=control` or `?force_booking_funnel=challenger`
 * Prefer the standard Abeet force when possible:
 * `?abeet[web_visitor_storage_booking_funnel]=challenger`
 */
const FORCE_BOOKING_FUNNEL_PARAM = 'force_booking_funnel';

type StorageBookingFunnelVariant =
  | typeof STORAGE_BOOKING_FUNNEL_CONTROL
  | typeof STORAGE_BOOKING_FUNNEL_CHALLENGER;

const asVariant = (value: unknown): StorageBookingFunnelVariant | undefined => {
  if (value === STORAGE_BOOKING_FUNNEL_CHALLENGER) {
    return STORAGE_BOOKING_FUNNEL_CHALLENGER;
  }
  if (value === STORAGE_BOOKING_FUNNEL_CONTROL) {
    return STORAGE_BOOKING_FUNNEL_CONTROL;
  }
  return undefined;
};

/**
 * Resolve a forced funnel variant from the URL, if any.
 * Supports both the convenience param and Abeet’s nested / bracket force params.
 * (`useQueryParams` uses URLSearchParams, so Abeet forces arrive as flat
 * `abeet[family]` keys.)
 */
function resolveForcedStorageBookingFunnel(
  query: Record<string, unknown> | undefined | null,
): StorageBookingFunnelVariant | undefined {
  if (!query) return undefined;

  const fromConvenience = asVariant(query[FORCE_BOOKING_FUNNEL_PARAM]);
  if (fromConvenience) return fromConvenience;

  const bracketKey = `abeet[${STORAGE_BOOKING_FUNNEL_TEST}]`;
  const fromBracket = asVariant(query[bracketKey]);
  if (fromBracket) return fromBracket;

  const abeet = query.abeet;
  if (abeet && typeof abeet === 'object' && !Array.isArray(abeet)) {
    return asVariant(
      (abeet as Record<string, unknown>)[STORAGE_BOOKING_FUNNEL_TEST],
    );
  }

  return undefined;
}

function searchParamsRecord(search: string): Record<string, unknown> {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const record: Record<string, unknown> = {};
  new URLSearchParams(normalized).forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

function isStorageBookingFunnelPath(): boolean {
  return /^\/book\/storage(?:\/|$)/.test(window.location.pathname);
}

function isChallengerTestVariables(variables: unknown): boolean {
  if (!variables || typeof variables !== 'object' || Array.isArray(variables)) {
    return false;
  }

  const challenger = (variables as Record<string, unknown>).challenger;
  return challenger === 'true' || challenger === true;
}

export function hasStorageBookingFunnelEnrollment(
  tests:
    | AbeetContextValue['tests']
    | Record<string, unknown>
    | null
    | undefined,
): boolean {
  return (
    !!tests &&
    Object.prototype.hasOwnProperty.call(tests, STORAGE_BOOKING_FUNNEL_TEST)
  );
}

/**
 * Whether challenger storage defaults should apply at checkout init.
 * Resolves URL force params, SSR-seeded Abeet (`serverData.tests`), then
 * client Abeet context (SPA navigation after `enrollStorageBookingFunnelTest`).
 */
export function isChallengerStorageBookingFunnelForInit(
  ssrTests?: Record<string, unknown> | null,
  clientTests?: AbeetContextValue['tests'],
): boolean {
  if (typeof window === 'undefined' || !isStorageBookingFunnelPath()) {
    return false;
  }

  const forced = resolveForcedStorageBookingFunnel(
    searchParamsRecord(window.location.search),
  );
  if (forced === STORAGE_BOOKING_FUNNEL_CHALLENGER) return true;
  if (forced === STORAGE_BOOKING_FUNNEL_CONTROL) return false;

  if (hasStorageBookingFunnelEnrollment(ssrTests)) {
    return isChallengerTestVariables(ssrTests![STORAGE_BOOKING_FUNNEL_TEST]);
  }

  if (hasStorageBookingFunnelEnrollment(clientTests)) {
    return isChallengerTestVariables(clientTests![STORAGE_BOOKING_FUNNEL_TEST]);
  }

  return false;
}

export interface StorageBookingFunnelAssignment {
  isChallenger: boolean;
  /** False while Abeet has not resolved the funnel variant (e.g. SPA nav from www). */
  isReady: boolean;
}

/** Analytics `flow_version` for storage checkout step transitions and funnel events. */
export function resolveStorageCheckoutFlowVersion(
  isChallenger: boolean,
): CheckoutVersion.Storage | CheckoutVersion.StorageV2 {
  return isChallenger ? CheckoutVersion.StorageV2 : CheckoutVersion.Storage;
}

/**
 * Enroll the storage booking funnel Abeet test before client-side navigation to
 * `/book/storage` so the variant is in context when the checkout route mounts.
 * Returns whether the enrolled (or URL-forced) variant is challenger.
 */
export async function enrollStorageBookingFunnelTest(
  execute: AbeetContextValue['execute'],
): Promise<boolean> {
  if (typeof window !== 'undefined') {
    const forced = resolveForcedStorageBookingFunnel(
      searchParamsRecord(window.location.search),
    );
    if (forced === STORAGE_BOOKING_FUNNEL_CHALLENGER) return true;
    if (forced === STORAGE_BOOKING_FUNNEL_CONTROL) return false;
  }

  if (!WT_VISITOR_TOKEN) return false;

  try {
    const result = await execute(
      STORAGE_BOOKING_FUNNEL_TEST,
      WT_VISITOR_TOKEN_KEY,
      WT_VISITOR_TOKEN,
    );
    return isChallengerTestVariables(result);
  } catch {
    // Checkout navigation should proceed even if Abeet enrollment fails.
    return false;
  }
}

/**
 * Classic (control) vs challenger assignment for the ENG-1021 storage booking funnel.
 * Enrolled on the server for `/book/storage` full page loads (CheckoutsController)
 * and on the client when entering checkout from www via SPA navigation.
 */
export function useStorageBookingFunnelAssignment(): StorageBookingFunnelAssignment {
  const query = useQueryParams();
  const forced = resolveForcedStorageBookingFunnel(query);

  const [challengerFromAbeet, loading] = useAbeetVisitorTest(
    STORAGE_BOOKING_FUNNEL_TEST,
    {
      skip: forced !== undefined,
      resolver: booleanResultResolver('challenger'),
    },
  );

  if (forced === STORAGE_BOOKING_FUNNEL_CHALLENGER) {
    return { isChallenger: true, isReady: true };
  }
  if (forced === STORAGE_BOOKING_FUNNEL_CONTROL) {
    return { isChallenger: false, isReady: true };
  }
  if (loading) {
    return { isChallenger: false, isReady: false };
  }
  return { isChallenger: challengerFromAbeet || false, isReady: true };
}
